import { type FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { cn } from '@/lib/utils';

/**
 * MarkdownViewer — renders FUNCTIONALITY.md (and other platform docs).
 *
 * Uses react-markdown with:
 *   - remark-gfm: GitHub-flavored (tables, task lists, strikethrough)
 *   - rehype-highlight: syntax highlighting for code blocks (JS, TS, SQL,
 *     JSON, bash)
 *
 * Styling is tuned for readability at long-form lengths. Prose widths cap
 * at ~72ch so lines do not run forever on wide screens.
 */

import 'highlight.js/styles/github.css';

export interface MarkdownViewerProps {
  source: string;
  className?: string;
}

export const MarkdownViewer: FC<MarkdownViewerProps> = ({ source, className }) => {
  return (
    <article
      className={cn(
        'max-w-[72ch] text-label leading-[1.7] text-text-primary',
        // Heading styles
        '[&>h1]:text-display [&>h1]:font-semibold [&>h1]:tracking-tight [&>h1]:mt-8 [&>h1]:mb-3 [&>h1:first-child]:mt-0',
        '[&>h2]:text-heading [&>h2]:font-semibold [&>h2]:tracking-tight [&>h2]:mt-7 [&>h2]:mb-2',
        '[&>h3]:text-body [&>h3]:font-semibold [&>h3]:mt-5 [&>h3]:mb-2',
        // Paragraph spacing
        '[&>p]:my-3',
        // Lists
        '[&>ul]:my-3 [&>ul]:pl-5 [&>ul]:list-disc',
        '[&>ol]:my-3 [&>ol]:pl-5 [&>ol]:list-decimal',
        '[&_li]:my-1',
        // Inline code
        '[&>p_code]:px-1.5 [&>p_code]:py-0.5 [&>p_code]:rounded [&>p_code]:bg-slate-900/5 [&>p_code]:text-[0.88em]',
        '[&_li_code]:px-1.5 [&_li_code]:py-0.5 [&_li_code]:rounded [&_li_code]:bg-slate-900/5 [&_li_code]:text-[0.88em]',
        // Code blocks
        '[&>pre]:my-4 [&>pre]:p-4 [&>pre]:bg-slate-900 [&>pre]:text-slate-100 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:text-caption',
        '[&>pre_code]:bg-transparent [&>pre_code]:text-inherit [&>pre_code]:p-0',
        // Tables
        '[&>table]:w-full [&>table]:my-4 [&>table]:border-collapse [&>table]:text-label',
        '[&>table_th]:bg-slate-50 [&>table_th]:text-left [&>table_th]:px-3 [&>table_th]:py-2 [&>table_th]:border [&>table_th]:border-glass-hairline [&>table_th]:font-semibold [&>table_th]:text-text-secondary',
        '[&>table_td]:px-3 [&>table_td]:py-2 [&>table_td]:border [&>table_td]:border-glass-hairline',
        // Blockquote
        '[&>blockquote]:border-l-4 [&>blockquote]:border-brand-blue/30 [&>blockquote]:pl-4 [&>blockquote]:text-text-secondary [&>blockquote]:italic [&>blockquote]:my-4',
        // Links
        '[&_a]:text-brand-blue [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80',
        // Horizontal rule
        '[&>hr]:my-6 [&>hr]:border-glass-hairline',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
        {source}
      </ReactMarkdown>
    </article>
  );
};

export default MarkdownViewer;
